import { useCallback, useMemo } from 'react';
import type { ScaleTime } from 'd3-scale';
import { startOfDay } from 'date-fns';

type Props = {
  xScale: ScaleTime<number, number>;
  valuations:
    | {
        fairValue: number | null;
        createdAt: string;
      }[]
    | null
    | undefined;
};

export const useNarrativeValuationHistory = ({ valuations, xScale }: Props) => {
  const { valuationsByDate, valuationTimestamps } = useMemo(() => {
    const oldestValuation = valuations?.at(-1);
    const newestValuation = valuations?.at(0);
    const domainStart = xScale.domain()[0].getTime();
    const domainEnd = xScale.domain()[1].getTime();

    const valuationsByDate: Record<number, number | null> =
      valuations?.reduce(
        (acc, v) => {
          acc[startOfDay(new Date(v.createdAt)).getTime()] = v.fairValue;
          return acc;
        },
        {
          [domainStart]: oldestValuation?.fairValue ?? null,
          [domainEnd]: newestValuation?.fairValue ?? null,
        }
      ) ?? {};
    const valuationTimestamps = Object.keys(valuationsByDate)
      .map(Number)
      .sort();

    return { valuationsByDate, valuationTimestamps };
  }, [valuations, xScale]);

  const findClosestFairValue = useCallback(
    (selectedTimestamp: number) => {
      const selectedFairValueTimestamp = valuationTimestamps.reduce(
        (result, current) => (selectedTimestamp >= current ? current : result),
        valuationTimestamps[0]
      );
      const lastValuationTimestamp = valuationTimestamps.at(-1);

      if (selectedFairValueTimestamp)
        return valuationsByDate[selectedFairValueTimestamp];

      return lastValuationTimestamp
        ? valuationsByDate[lastValuationTimestamp]
        : null;
    },
    [valuationTimestamps, valuationsByDate]
  );

  return {
    findClosestFairValue,
    valuationsByDate,
    valuationTimestamps,
  };
};
